import { fork, put, race, take, takeLatest } from 'redux-saga/effects';

import {
  SettingsPlansActionTypes,
  settingsPlansSelector,
} from '../redux/server/settingsPlans';
import {
  FetchSettingsPlansDetailsOnYearMonthChangeAction,
  SettingsPlansDetailsActionCreators,
  SettingsPlansDetailsActionTypes,
} from '../redux/server/settingsPlansDetails';
import { planSettingsIsPredictionActiveSelector } from '../redux/ui/planSettings';
import { Api } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { handleErrorSaga } from './errorSaga';

/**
 * 年月変更時に予定詳細データを再取得するSaga
 * 店舗一覧の取得完了を待ってから、新しい年月で予定詳細データを取得する。
 *
 * 予測更新中（`planSettingsIsPredictionActiveSelector === true`）のまま年月が切り替わった場合は、
 * 詳細データの取得成功を待って、選択中の店舗の最新予測（latest）を再取得する。
 * 詳細取得成功の時点で再度 `planSettingsIsPredictionActiveSelector` を参照し、
 * その間にキャンセル等で false になっていれば latest は取りに行かない。
 * これにより「予測更新中」状態を維持しつつ未来年月の予測を確認できるようにする。
 * @param action FetchSettingsPlansDetailsOnYearMonthChangeAction
 */
export function* fetchSettingsPlansDetailsOnYearMonthChangeSaga(
  action: FetchSettingsPlansDetailsOnYearMonthChangeAction
) {
  const { year, month, hallCode } = action.payload;

  try {
    // 店舗一覧の取得完了または失敗を待つ
    const settingsPlansResult: { success?: unknown; failure?: unknown } =
      yield race({
        success: take(SettingsPlansActionTypes.FETCH_SETTINGS_PLANS_SUCCESS),
        failure: take(SettingsPlansActionTypes.FETCH_SETTINGS_PLANS_FAILURE),
      });

    // 失敗した場合は処理を中断
    if (settingsPlansResult.failure) {
      return;
    }

    // 店舗情報を取得
    const settingsPlans = yield* customSelect(settingsPlansSelector);
    const hall = settingsPlans?.data.find(
      (h) => h.parameter.hallCode === hallCode
    );

    // 店舗情報が存在しない場合は処理を中断
    if (!hall) {
      return;
    }

    // 新しい年月で予定詳細データを取得
    yield put(
      SettingsPlansDetailsActionCreators.fetchSettingsPlansDetailsAction({
        year,
        month,
        hallCode,
      })
    );

    // 予測更新中でなければ、詳細データの取得後は何もしない（通常の年月切替）
    const isPredictionActive = yield* customSelect(
      planSettingsIsPredictionActiveSelector
    );
    if (!isPredictionActive) {
      return;
    }

    // 詳細データの取得完了（SUCCESS）もしくは失敗（RENEW = reducer 的に空戻し）を待つ
    // RENEW_SETTINGS_PLANS_DETAILS は失敗時に state をリセットするアクションのため、
    // これを失敗シグナルとして扱い race を抜ける。
    const detailsResult: { success?: unknown; failure?: unknown } = yield race({
      success: take(
        SettingsPlansDetailsActionTypes.FETCH_SETTINGS_PLANS_DETAILS_SUCCESS
      ),
      failure: take(
        SettingsPlansDetailsActionTypes.RENEW_SETTINGS_PLANS_DETAILS
      ),
    });

    if (detailsResult.failure) {
      // 詳細取得に失敗したときは最新予測も取りに行かない
      return;
    }

    // 待機中に予測更新が解除されていれば latest は取りに行かない
    const isPredictionActiveAfterDetailsSuccess = yield* customSelect(
      planSettingsIsPredictionActiveSelector
    );
    if (!isPredictionActiveAfterDetailsSuccess) {
      return;
    }

    // 詳細取得の成功を受けて最新予測を再取得する
    yield put(
      SettingsPlansDetailsActionCreators.fetchLatestSettingsPlansDetailsAction({
        year,
        month,
        hallCode,
      })
    );
  } catch (error: unknown) {
    yield fork(handleErrorSaga, error);
  }
}

function* handleFetchSettingsPlansDetailsOnYearMonthChangeSaga() {
  yield takeLatest(
    SettingsPlansDetailsActionTypes.FETCH_SETTINGS_PLANS_DETAILS_ON_YEAR_MONTH_CHANGE,
    fetchSettingsPlansDetailsOnYearMonthChangeSaga
  );
}

export function* planSettingsSagas(_context: { api: Api }) {
  yield fork(handleFetchSettingsPlansDetailsOnYearMonthChangeSaga);
}
